import Container from "@client/content/Containers/container";
import { getRootContainerProps } from "@client/content/lib/helper";
import { type RootContainerProps } from "@client/content/types";
import { sort } from "@client/lib/array";
import { IconType } from "@client/types";
import RichEditor from "@common/components/Inputs/RichEditor";
import Search from "@common/controls/Inputs/Search/component";
import { i18nFormat } from "@common/lib/i18n";
import { identity, isNull } from "lodash";
import * as React from "react";
import { ModuleSecondaryButton } from "../Button";
import KnoledgeBaseCategoryAccordion from "./Accordion/component";
import Breadcrumbs from "./Breadcrumbs";
import KnoledgeBaseCategoryNested from "./Nested/component";
import KnoledgeBaseCategoryTiles from "./Tiles/component";
import { type KnowledgeBase } from "./types";

import { useI18n } from "@common/hooks/i18n";
import "./style.less";

export type NavigationType = "acccordion" | "nested" | "tiles";

export interface ActionProps {
  setActiveItem: (item: KnowledgeBase.View.ActiveItem | null) => void;
}

export type KnoledgeBaseComponentProps = RootContainerProps & {
  activeItem: KnowledgeBase.View.ActiveItem | null;
  articles: KnowledgeBase.Api.Article[];
  categories: KnowledgeBase.Api.ArticleCategory[];
  columns: number;
  fieldStyle: React.CSSProperties;
  navigationType: NavigationType;
  style: React.CSSProperties;
};

export default (props: ActionProps & KnoledgeBaseComponentProps) => {
  const i18n = useI18n();

  const [searchText, setSearchText] = React.useState("");

  const back = React.useCallback(() => {
    if (isNull(props.activeItem)) return;

    const id =
      props.activeItem.type === "category"
        ? props.activeItem.category!.parentArticleCategoryId
        : props.activeItem.article!.articleCategoryId;

    const newCategory = props.categories!.find((x) => x.articleCategoryId === id);

    if (newCategory) {
      props.setActiveItem({
        type: "category",
        category: newCategory
      });
    } else {
      props.setActiveItem(null);
    }
  }, [props.activeItem, props.setActiveItem, props.categories]);

  const sortedArticles = React.useMemo(() => sort(props.articles ?? [], { key: "sortOrder" }), [props.articles]);

  const filteredCategories = React.useMemo(
    () => props.categories.filter((a) => !searchText || a.name.toLowerCase().includes(searchText.toLowerCase())),
    [props.categories, searchText]
  );

  const activeCategorySubcategories = React.useMemo(() => {
    if (isNull(props.activeItem))
      return sort(
        filteredCategories.filter((a) => !a.parentArticleCategoryId),
        { key: "sortOrder" }
      );

    if (props.activeItem.type === "category")
      return sort(
        filteredCategories.filter(
          (a) =>
            a.parentArticleCategoryId ===
            (props.activeItem as KnowledgeBase.View.ActiveCategory).category.articleCategoryId
        ),
        { key: "sortOrder" }
      );

    return [];
  }, [filteredCategories, props.activeItem]);

  const activeCategoryArticles = React.useMemo(() => {
    if (isNull(props.activeItem)) return [];

    if (props.activeItem != null && props.activeItem.type === "category")
      return sortedArticles.filter(
        (a) =>
          a.articleCategoryId === (props.activeItem as KnowledgeBase.View.ActiveCategory).category.articleCategoryId
      );

    return [];
  }, [sortedArticles, props.activeItem]);

  const getCategoryArticlesCount = React.useCallback(
    (category: KnowledgeBase.Api.ArticleCategory): string => {
      const count = props.articles?.filter(
        (article) => article.articleCategoryId === category.articleCategoryId
      ).length;
      if (!count) return "";
      return count === 1 ? i18n.oneArticle : i18nFormat(i18n.nArticles, ["N", count]);
    },
    [props.articles]
  );

  return (
    <Container {...getRootContainerProps(props)} className="wsc_content_knowledge_base">
      {props.activeItem && (props.navigationType !== "acccordion" || props.activeItem.type === "article") ? (
        <>
          <Breadcrumbs
            activeItem={props.activeItem}
            articleCategories={props.categories}
            onCategoryClick={(category) => {
              if (isNull(category)) props.setActiveItem(null);
              else props.setActiveItem({ type: "category", category: category });
            }}
          />
          <div className="knowledge_base_title">
            <ModuleSecondaryButton {...getRootContainerProps(props)} icon={IconType.ArrowLeft} onClick={back} />
          </div>
          <h3>
            {props.activeItem.type === "article" ? props.activeItem.article.title : props.activeItem.category.name}
          </h3>
        </>
      ) : (
        <div className="knowledge_base_title">
          <h3>{i18n.knowledgeBase}</h3>
          <Search value={searchText} onChange={setSearchText} onChangeTrigger={0} style={props.fieldStyle} />
        </div>
      )}
      {props.activeItem?.type === "article" ? (
        props.activeItem.content && (
          <RichEditor isReadonly isInline onChange={identity} value={props.activeItem.content} />
        )
      ) : props.navigationType === "acccordion" ? (
        <KnoledgeBaseCategoryAccordion
          activeCategory={props.activeItem?.category ?? null}
          articles={sortedArticles}
          categories={filteredCategories!}
          onArticleClick={(x) => props.setActiveItem({ type: "article", article: x })}
          style={props.style}
        />
      ) : props.navigationType === "nested" ? (
        <KnoledgeBaseCategoryNested
          articleCategories={activeCategorySubcategories}
          articles={activeCategoryArticles}
          getCategoryArticlesCount={getCategoryArticlesCount}
          setActiveItemState={(x) => (props.setActiveItem(x), x?.type === "article")}
          style={props.style}
        />
      ) : props.navigationType === "tiles" ? (
        <KnoledgeBaseCategoryTiles
          articleCategories={activeCategorySubcategories}
          articles={activeCategoryArticles}
          getCategoryArticlesCount={getCategoryArticlesCount}
          numberOfColumns={props.columns}
          setActiveItemState={props.setActiveItem}
          style={props.style}
        />
      ) : null}
    </Container>
  );
};
